import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { X, CheckCircle2, RotateCcw, Ban, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useEosTodo } from '../hooks/useEosTodo';
import {
  useCreateEosTodo, useUpdateEosTodo, useCompleteEosTodo,
  useReopenEosTodo, useCancelEosTodo, useDeleteEosTodo,
} from '../hooks/useEosTodoMutations';
import { TodoStatusBadge } from './TodoStatusBadge';
import { TodoFormFields, type TodoFormState } from './TodoFormFields';
import type { EosTodo, EosTodoPriority } from '../types';

interface Props {
  todoId: string | null;
  mode: 'view' | 'create';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
  ownerOptions: { id: string; label: string }[];
  teamOptions: { id: string; label: string }[];
}

const EMPTY: TodoFormState = {
  title: '',
  description: '',
  ownerId: '',
  teamId: '',
  dueDate: (() => { const d = new Date(); d.setUTCDate(d.getUTCDate() + 7); return d.toISOString().slice(0, 10); })(),
  priority: 'medium',
};

function toForm(t: EosTodo): TodoFormState {
  return {
    title: t.title,
    description: t.description ?? '',
    ownerId: t.ownerId,
    teamId: t.teamId ?? '',
    dueDate: t.dueDate,
    priority: t.priority,
  };
}

export function TodoDetailPanel({
  todoId, mode, open, onOpenChange, onCreated, ownerOptions, teamOptions,
}: Props) {
  const { data: todo } = useEosTodo(mode === 'view' ? todoId : null);
  const [form, setForm] = useState<TodoFormState>(EMPTY);

  const create = useCreateEosTodo();
  const update = useUpdateEosTodo();
  const complete = useCompleteEosTodo();
  const reopen = useReopenEosTodo();
  const cancel = useCancelEosTodo();
  const remove = useDeleteEosTodo();

  useEffect(() => {
    if (mode === 'create') setForm(EMPTY);
    else if (todo) setForm(toForm(todo));
  }, [mode, todo]);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (!form.ownerId) { toast.error('Owner required'); return; }
    if (mode === 'create') {
      const row = await create.mutateAsync({
        title: form.title,
        description: form.description || undefined,
        owner_id: form.ownerId,
        team_id: form.teamId || undefined,
        due_date: form.dueDate,
        priority: form.priority as EosTodoPriority,
      });
      onCreated(row.id);
    } else if (todo) {
      await update.mutateAsync({
        id: todo.id,
        data: {
          title: form.title,
          description: form.description,
          owner_id: form.ownerId,
          team_id: form.teamId || null,
          due_date: form.dueDate,
          priority: form.priority as EosTodoPriority,
        },
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-3 border-b">
        <div className="flex items-center gap-2">
          {mode === 'view' && todo && <TodoStatusBadge status={todo.status} />}
          <h3 className="text-sm font-semibold">{mode === 'create' ? 'New To-Do' : 'To-Do'}</h3>
        </div>
        <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-4 space-y-4 overflow-auto flex-1">
        <TodoFormFields
          value={form}
          onChange={setForm}
          ownerOptions={ownerOptions}
          teamOptions={teamOptions}
        />

        {mode === 'view' && todo && (
          <div className="flex flex-wrap gap-2">
            {todo.status !== 'done' && (
              <Button size="sm" variant="secondary" onClick={() => complete.mutate(todo.id)}>
                <CheckCircle2 className="h-4 w-4 mr-1" /> Complete
              </Button>
            )}
            {todo.status === 'done' && (
              <Button size="sm" variant="secondary" onClick={() => reopen.mutate(todo.id)}>
                <RotateCcw className="h-4 w-4 mr-1" /> Reopen
              </Button>
            )}
            {todo.status !== 'cancelled' && (
              <Button size="sm" variant="outline" onClick={() => cancel.mutate(todo.id)}>
                <Ban className="h-4 w-4 mr-1" /> Cancel
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                if (confirm('Delete this To-Do?')) {
                  remove.mutate(todo.id);
                  onOpenChange(false);
                }
              }}
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          </div>
        )}
      </div>

      <div className="p-3 border-t flex justify-end gap-2">
        <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
